/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Spinner,
    SecurityHelper,
    Panel,
    StyledModal,
    ModalActions,
    ButtonGroup,
    Button,
    HtmlParser,
    Text,
    Divider,
    Heading,
    Stack,
    FlexRow,
    FlexCol,
    ContentContainerBodyShift,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import get from 'lodash/get';
import { gnoment } from 'shared-ui/gnoment';
import { authModalActions } from 'shared-ui/auth/redux';
import {
    permitCategoriesToString,
    readRuleAsMoment,
    hasLotteryPreferenceDateDivisionDuration,
    hasLotteryPreferenceDateDivisionGroupSize,
    isInvalidLotteryAward,
} from '../utils/utilsNew';
import ErrorNotification from '../components/ErrorNotification';
import LotteryWinnerPrompt from '../components/lotteryWinnerConfirmationPage/LotteryWinnerPrompt';
import * as pActions from '../actions/permit';
import * as types from '../constants';

const propTypes = {
    router: COMPAT_ROUTER_PROP_TYPES,
    account: PropTypes.shape({}),
    dispatch: PropTypes.func,
    fetchIssuanceError: PropTypes.shape({
        msg: PropTypes.string,
        code: PropTypes.string,
    }),
    fetchLotteryError: PropTypes.any,
    permitError: PropTypes.shape({}),
    issuanceError: PropTypes.shape({}),
    fetchRecareaError: PropTypes.shape({}),
    issuance: PropTypes.shape({
        id: PropTypes.string,
        status: PropTypes.number,
        order_id: PropTypes.string,
        start_date: PropTypes.string,
        division_id: PropTypes.string,
        permit_id: PropTypes.string,
        selected_lottery_preference: PropTypes.object,
    }),
    permit: PropTypes.shape({
        addresses: PropTypes.arrayOf(
            PropTypes.shape({
                city: PropTypes.string,
                state_code: PropTypes.string,
            })
        ),
        name: PropTypes.string,
        type: PropTypes.number,
        description: PropTypes.shape({
            need_to_know_permit: PropTypes.string,
            fee_policy: PropTypes.string,
            no_show_policy: PropTypes.string,
            cancellation_policy: PropTypes.string,
        }),
        divisions: PropTypes.object,
        category: PropTypes.string,
        rules: PropTypes.array,
        time_zone: PropTypes.string,
    }),
    recarea: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        })
    ),
};

export class LotteryWinnerConfirmationPage extends React.Component {
    static propTypes = propTypes;

    constructor() {
        super();
        this.state = {
            modalShown: false,
            isDeclineModalOpen: false,
            isDeclineLoading: false,
        };

        this.handleLotteryAccept = this.handleLotteryAccept.bind(this);
    }

    componentDidMount() {
        const loggedin = SecurityHelper.isLoggedIn();
        if (!loggedin) {
            this.setState({ modalShown: true });
            this.props.dispatch(authModalActions.openLoginModal());
            return;
        }
        this.fetchData();
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.account && this.props.account && this.state.modalShown) {
            this.fetchData();
        }
    }

    componentWillUnmount() {
        const { dispatch } = this.props;

        dispatch(pActions.clearError());
        dispatch(pActions.clearFetchIssuanceError());
        dispatch(pActions.clearRecareaError());
        dispatch(pActions.clearFetchLotteryError());
    }

    handleLotteryAccept() {
        const loggedin = SecurityHelper.isLoggedIn();
        if (!loggedin) {
            this.props.dispatch(authModalActions.openLoginModal());
            return;
        }
        this.redirectToOrderDetailsPage();
    }

    getRecAreaName() {
        const { recarea } = this.props;
        if (recarea && recarea.name) {
            return recarea.name;
        }
        return '';
    }

    lotteryDecline = () => {
        const { id, permit_id: permitId } = this.props.issuance;
        this.setState({ isDeclineLoading: true });
        this.props.dispatch(pActions.cancelIssuance(id, permitId)).then(() => {
            window.location.assign('/account/reservations/lotteries');
        });
    };

    openDeclineModal = () => {
        this.setState({ isDeclineModalOpen: true });
    };

    closeModal = () => {
        this.setState({ isDeclineModalOpen: false });
    };

    fetchData() {
        const permitId = this.props.router.params.permitId;
        const issuanceId = this.props.router.searchParams.get('id');
        // fire off the following non-blocking function
        this.props.dispatch(
            (function parseAndLoadParams() {
                return (dispatch, getState) => {
                    // TODO - handle missing permitId / queryId - set permitId / issuanceId error states
                    dispatch(pActions.fetchPermit(permitId)).then(() => {
                        let state = getState();

                        const permit = state.permit.result;
                        const { recareas } = permit;
                        if (recareas && recareas.length) {
                            dispatch(pActions.fetchRecarea(recareas[0]));
                        }

                        dispatch(pActions.fetchIssuance(issuanceId, permit.id)).then(
                            () => {
                                state = getState();
                                const issuance = state.permit.issuance;
                                const lotteryId = get(issuance, 'lottery_id', false);
                                if (lotteryId) {
                                    dispatch(pActions.fetchLottery(lotteryId));
                                }
                            }
                        );
                    });
                };
            })()
        );
    }

    redirectToOrderDetailsPage() {
        const reservationId = `${this.props.issuance.id}${types.RES_ID_DELIMITER}${this.props.issuance.permit_id}`;
        window.location.assign(
            `/permits/${this.props.issuance.permit_id}/registration/${reservationId}`
        ); // could this be a router navigate?
    }

    renderNeedToKnow() {
        const { need_to_know_permit: needToKnowPermit } = this.props.permit.description;
        return (
            <Panel label="Need to Know">
                <Text fontWeight="bold">Need To Know</Text>
                {this.renderContent(needToKnowPermit)}
            </Panel>
        );
    }

    renderFeePolicy() {
        const { fee_policy: feePolicy } = this.props.permit.description;
        return (
            <Panel label="Fee Policy">
                <Text fontWeight="bold">Fee Policy</Text>
                {this.renderContent(feePolicy)}
            </Panel>
        );
    }

    renderNoShowPolicy() {
        const { no_show_policy: noShowPolicy } = this.props.permit.description;
        return (
            <Panel label="No Show Policy">
                <Text fontWeight="bold">No Show Policy</Text>
                {this.renderContent(noShowPolicy)}
            </Panel>
        );
    }

    renderCancellationPolicy() {
        const { cancellation_policy: cancellationPolicy } = this.props.permit.description;
        return (
            <Panel label="Cancellation Policy">
                <Text fontWeight="bold">Cancellation Policy</Text>
                {this.renderContent(cancellationPolicy)}
            </Panel>
        );
    }

    renderContent(body) {
        if (body) {
            return (
                <div>
                    <HtmlParser html={body} />
                </div>
            );
        }
        return null;
    }

    render() {
        if (
            !this.props.permit ||
            Object.keys(this.props.permit).length === 0 ||
            !this.props.issuance ||
            Object.keys(this.props.issuance).length === 0
        ) {
            // issuance error message
            let errorMessage = types.ERROR_DEFAULT;
            if (this.props.fetchIssuanceError) {
                switch (this.props.fetchIssuanceError.code) {
                    case 401:
                        errorMessage = types.ERROR_UNAUTHORIZED;
                        break;
                    default:
                        errorMessage = types.ERROR_DEFAULT;
                        break;
                }
            }
            if (this.props.fetchIssuanceError || this.props.fetchLotteryError) {
                return (
                    <div className="rec-page">
                        <h1>Page Unavailable.</h1>
                        <p>{errorMessage}</p>
                    </div>
                );
            }
            // loading
            return (
                <div className="permit-content-outer-wrap">
                    <Spinner />
                </div>
            );
        }

        // invalid state
        if (isInvalidLotteryAward(this.props)) {
            return (
                <div className="rec-page">
                    <h1>Warning - this page has expired.</h1>
                </div>
            );
        }

        const { addresses } = this.props.permit;
        let permitLocation;
        if (
            addresses &&
            addresses.length &&
            addresses[0].city &&
            addresses[0].state_code
        ) {
            permitLocation = `${addresses[0].city}, ${addresses[0].state_code}`;
        } else if (addresses && addresses.length && addresses[0].state_code) {
            permitLocation = addresses[0].state_code;
        }

        const { type } = this.props.permit;
        let permitCategory;
        if (`${type}`) {
            permitCategory = permitCategoriesToString.get(`${type}`);
        }

        const enableLengthOfStay = hasLotteryPreferenceDateDivisionDuration(
            this.props.permit,
            undefined
        );
        const enableGroupSize = hasLotteryPreferenceDateDivisionGroupSize(
            this.props.permit,
            undefined
        );
        const enableNoResponseText = this.props.permit.category === 0;

        // full rendering
        return (
            <div className="rec-page">
                <StyledModal
                    size="md"
                    heading="Decline Reservation"
                    shouldShowCloseButton
                    shouldActionsStickInMobile
                    onRequestClose={this.closeModal}
                    isOpen={this.state.isDeclineModalOpen}
                >
                    <p>
                        Are you sure you want to decline your lottery win? This action
                        cannot be undone.
                    </p>
                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                appearance="tertiary"
                                onClick={this.closeModal}
                                className="rec-button-tertiary"
                            >
                                Cancel Changes
                            </Button>
                            <Button
                                isLoading={this.state.isDeclineLoading}
                                onClick={this.lotteryDecline}
                            >
                                Confirm Changes
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </StyledModal>

                <ContentContainerBodyShift>
                    <FlexRow className="rec-page rec-venue-page">
                        <FlexCol lg={8} className="rec-venue-page-left-rail">
                            <div id="page-body">
                                <ErrorNotification
                                    errors={[
                                        this.props.permitError,
                                        this.props.issuanceError,
                                        this.props.fetchIssuanceError,
                                    ]}
                                />

                                <Stack space="sm">
                                    <Heading headingLevel={1} appearance="h3">
                                        Reservation Confirmation
                                    </Heading>
                                    <Heading headingLevel={2} appearance="h5">
                                        You have been awarded a reservation for a permit!
                                    </Heading>
                                    <Text>
                                        Please confirm that you will accept or decline the
                                        reservation.
                                    </Text>

                                    {enableNoResponseText && (
                                        <Text fontWeight="bold">
                                            Failure to provide a response will potentially
                                            restrict you from holding a permit on this
                                            river for ONE YEAR.
                                        </Text>
                                    )}
                                </Stack>
                                <Divider />

                                {this.renderNeedToKnow()}
                                {this.renderFeePolicy()}
                                {this.renderCancellationPolicy()}
                                {this.renderNoShowPolicy()}
                            </div>
                        </FlexCol>
                        <FlexCol lg={4} className="mt-4">
                            <LotteryWinnerPrompt
                                handleLotteryAccept={this.handleLotteryAccept}
                                handleLotteryDecline={this.openDeclineModal}
                                orderId={this.props.issuance.order_id}
                                permitName={this.props.permit.name}
                                permitLocation={permitLocation}
                                permitCategory={permitCategory}
                                recAreaName={this.getRecAreaName()}
                                startDate={gnoment(this.props.issuance.start_date)
                                    .utc()
                                    .startOf('day')
                                    .format(types.MONTH_DAY_YEAR_NUM_FORMAT)}
                                confirmDate={readRuleAsMoment(
                                    'LotteryDeadlineToConfirm',
                                    this.props.permit.rules,
                                    this.props.permit.time_zone,
                                    this.props.issuance.division_id
                                ).format(types.MONTH_DAY_YEAR_NUM_FORMAT)}
                                selectedLotteryPreference={
                                    this.props.issuance.selected_lottery_preference
                                }
                                divisions={this.props.permit.divisions}
                                enableLengthOfStay={enableLengthOfStay}
                                enableGroupSize={enableGroupSize}
                            />
                        </FlexCol>
                    </FlexRow>
                </ContentContainerBodyShift>
            </div>
        );
    }
}

function select(state) {
    return {
        permit: state.permit.result,
        permitError: state.permit.error,
        recarea: state.permit.recarea,
        account: state.auth.account,
        issuance: state.permit.issuance,
        issuanceError: state.permit.issuance.error,
        lottery: state.permit.lottery,
        fetchIssuanceError: state.permit.fetch_issuance_error,
        fetchRecareaError: state.permit.recarea_err,
        fetchLotteryError: state.permit.lottery_err,
    };
}

export default withCompatRouter(connect(select)(LotteryWinnerConfirmationPage));
